import React from 'react';

type Tone = 'ok' | 'warning' | 'alert' | 'neutral' | 'idle';
type Size = 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<Size, { wrap: string; core: string; ringMid: string; ringOuter: string }> = {
  sm: { wrap: 'w-24 h-24', core: 'w-12 h-12', ringMid: 'w-20 h-20', ringOuter: 'w-24 h-24' },
  md: { wrap: 'w-40 h-40', core: 'w-20 h-20', ringMid: 'w-32 h-32', ringOuter: 'w-40 h-40' },
  lg: { wrap: 'w-64 h-64', core: 'w-32 h-32', ringMid: 'w-52 h-52', ringOuter: 'w-64 h-64' },
  xl: { wrap: 'w-80 h-80', core: 'w-40 h-40', ringMid: 'w-64 h-64', ringOuter: 'w-80 h-80' },
};

const toneMap: Record<Tone, { core: string; ring: string }> = {
  ok: { core: 'bg-brand-secondary', ring: 'bg-brand-secondary-soft' },
  warning: { core: 'bg-status-late', ring: 'bg-status-late-soft' },
  alert: { core: 'bg-status-alert', ring: 'bg-status-alert-soft' },
  neutral: { core: 'bg-brand-primary', ring: 'bg-brand-primary-soft' },
  idle: { core: 'bg-gray-300', ring: 'bg-gray-100' },
};

type Props = {
  tone?: Tone;
  size?: Size;
  animate?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export const PulseCircle: React.FC<Props> = ({
  tone = 'ok',
  size = 'lg',
  animate = true,
  className = '',
  children,
}) => {
  const s = sizeMap[size];
  const t = toneMap[tone];
  return (
    <div
      aria-hidden="true"
      className={`relative inline-flex items-center justify-center ${s.wrap} ${className}`}
    >
      <span
        className={`absolute rounded-full ${t.ring} ${s.ringOuter} ${
          animate ? 'animate-pulse-ring' : 'opacity-30'
        }`}
      />
      <span
        className={`absolute rounded-full ${t.ring} ${s.ringMid} ${
          animate ? 'animate-pulse-ring-mid' : 'opacity-50'
        }`}
      />
      <span
        className={`relative rounded-full ${t.core} ${s.core} ${
          animate ? 'animate-float-soft' : ''
        } shadow-elev-2 inline-flex items-center justify-center`}
      >
        {children}
      </span>
    </div>
  );
};
